import { useState, useCallback } from 'react';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  pointerWithin,
  useSensor,
  useSensors,
  closestCorners,
  type CollisionDetection,
} from '@dnd-kit/core';
import { SortableContext, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { ColumnComponent } from './ColumnComponent';
import { CardOverlay } from './CardOverlay';
import { MergeCardModal, shouldSkipMergeConfirmation } from './modals/MergeCardModal';
import type { Board, Card, Column } from '../types/board';

interface BoardDisplayProps {
  board: Board;
  userName: string;
  userId: string;
  onAddCard: (columnId: string, content: string, author: string) => Promise<void>;
  onMoveCard: (cardId: string, targetColumnId: string, targetIndex?: number) => Promise<void>;
  onDeleteCard?: (cardId: string) => Promise<void>;
  onMergeCards?: (sourceCardId: string, targetCardId: string) => Promise<void>;
  onVoteCard?: (cardId: string) => Promise<void>;
  onRemoveCardVote?: (cardId: string) => Promise<void>;
  onReorderColumns?: (columnIds: string[]) => Promise<void>;
  onEditCard?: (card: Card) => void;
  onEditColumn?: (column: Column) => void;
  onDeleteColumn?: (column: Column) => void;
  enableMergeCards?: boolean;
  isAdmin?: boolean;
  revealAllCards?: boolean;
}

interface MergeState {
  sourceCard: Card;
  targetCard: Card;
}

const pointerThenCorners: CollisionDetection = (args) => {
  const pointerCollisions = pointerWithin(args);
  return pointerCollisions.length > 0 ? pointerCollisions : closestCorners(args);
};

function findCardById(columns: Column[], cardId: string): Card | null {
  for (const column of columns) {
    const card = column.cards.find((candidate) => candidate.id === cardId);
    if (card) return card;
  }
  return null;
}

function findColumnById(columns: Column[], columnId: string): Column | null {
  return columns.find((column) => column.id === columnId) ?? null;
}

function canDragCard(card: Card, isAdmin: boolean, userId: string): boolean {
  return isAdmin || card.isAuthor === true || card.createdBy === userId;
}

function getCardDropTargetIndex(
  event: DragEndEvent,
  targetColumn: Column,
  targetCard: Card,
  activeCard: Card
): number {
  const overIndex = targetColumn.cards.findIndex((card) => card.id === targetCard.id);
  if (overIndex === -1) return targetColumn.cards.length;

  const activeRect = event.active.rect.current.translated;
  const activeCenterY = activeRect
    ? activeRect.top + activeRect.height / 2
    : event.over?.rect.top ?? 0;
  const overCenterY = event.over
    ? event.over.rect.top + event.over.rect.height / 2
    : activeCenterY;
  const targetOffset = activeCenterY > overCenterY ? 1 : 0;
  let targetIndex = overIndex + targetOffset;

  if (activeCard.columnId === targetColumn.id) {
    const activeIndex = targetColumn.cards.findIndex((card) => card.id === activeCard.id);
    if (activeIndex !== -1 && activeIndex < targetIndex) {
      targetIndex -= 1;
    }
  }

  return targetIndex;
}

function isMergeDrop(event: DragEndEvent): boolean {
  if (!event.over) return false;
  const activeRect = event.active.rect.current.translated;
  if (!activeRect) return false;

  const activeCenterY = activeRect.top + activeRect.height / 2;
  const mergeTop = event.over.rect.top + event.over.rect.height * 0.35;
  const mergeBottom = event.over.rect.top + event.over.rect.height * 0.65;
  return activeCenterY >= mergeTop && activeCenterY <= mergeBottom;
}

export function BoardDisplay({
  board,
  userName,
  userId,
  onAddCard,
  onMoveCard,
  onDeleteCard,
  onMergeCards,
  onVoteCard,
  onRemoveCardVote,
  onReorderColumns,
  onEditCard,
  onEditColumn,
  onDeleteColumn,
  enableMergeCards = false,
  isAdmin = false,
  revealAllCards = false,
}: BoardDisplayProps): JSX.Element {
  const [activeCard, setActiveCard] = useState<Card | null>(null);
  const [activeColumn, setActiveColumn] = useState<Column | null>(null);
  const [mergeState, setMergeState] = useState<MergeState | null>(null);
  const [isMerging, setIsMerging] = useState(false);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } })
  );

  const handleDragStart = useCallback((event: DragStartEvent) => {
    const { type, card, column } = event.active.data.current || {};
    if (type === 'card' && card) {
      const activeCardData = card as Card;
      if (!canDragCard(activeCardData, isAdmin, userId)) return;
      setActiveCard(activeCardData);
      setActiveColumn(null);
    } else if (type === 'column' && column) {
      setActiveColumn(column as Column);
      setActiveCard(null);
    }
  }, [isAdmin, userId]);

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      setActiveCard(null);
      setActiveColumn(null);

      if (!over) return;

      const activeData = active.data.current;
      const overData = over.data.current;

      // Handle column reordering (admin only)
      if (activeData?.type === 'column' && isAdmin && onReorderColumns) {
        const activeColumnId = activeData.column?.id;
        const overCard = overData?.type === 'card' && overData.card
          ? (overData.card as Card)
          : findCardById(board.columns, String(over.id));
        const overColumn = overData?.type === 'column' && overData.column
          ? (overData.column as Column)
          : findColumnById(board.columns, String(over.id));
        const overColumnId = overColumn?.id ?? overCard?.columnId;

        if (activeColumnId && overColumnId && activeColumnId !== overColumnId) {
          const columnIds = [...board.columns]
            .sort((a, b) => a.order - b.order)
            .map((c) => c.id);
          const activeIndex = columnIds.indexOf(activeColumnId);
          const overIndex = columnIds.indexOf(overColumnId);

          if (activeIndex !== -1 && overIndex !== -1) {
            const newColumnIds = [...columnIds];
            newColumnIds.splice(activeIndex, 1);
            newColumnIds.splice(overIndex, 0, activeColumnId);
            onReorderColumns(newColumnIds);
          }
        }
        return;
      }

      // Handle card operations
      const activeCardData = activeData?.card as Card | undefined;
      if (!activeCardData) return;
      if (!canDragCard(activeCardData, isAdmin, userId)) return;

      const targetCard = overData?.type === 'card' && overData.card
        ? (overData.card as Card)
        : findCardById(board.columns, String(over.id));

      if (targetCard) {
        if (
          isAdmin &&
          enableMergeCards &&
          onMergeCards &&
          targetCard.id !== activeCardData.id &&
          isMergeDrop(event)
        ) {
          if (shouldSkipMergeConfirmation()) {
            onMergeCards(activeCardData.id, targetCard.id);
          } else {
            setMergeState({ sourceCard: activeCardData, targetCard });
          }
          return;
        }

        const targetColumn = findColumnById(board.columns, targetCard.columnId);
        if (targetColumn) {
          const targetIndex = getCardDropTargetIndex(event, targetColumn, targetCard, activeCardData);
          onMoveCard(activeCardData.id, targetColumn.id, targetIndex);
        }
        return;
      }

      // Dropped on column
      const targetColumn = overData?.type === 'column' && overData.column
        ? (overData.column as Column)
        : findColumnById(board.columns, String(over.id));
      const targetColumnId = targetColumn?.id;
      if (targetColumn && targetColumnId) {
        onMoveCard(activeCardData.id, targetColumnId, targetColumn.cards.length);
      }
    },
    [board.columns, onMoveCard, onMergeCards, onReorderColumns, isAdmin, userId, enableMergeCards]
  );

  const handleMergeConfirm = useCallback(async () => {
    if (mergeState && onMergeCards) {
      setIsMerging(true);
      try {
        await onMergeCards(mergeState.sourceCard.id, mergeState.targetCard.id);
      } finally {
        setIsMerging(false);
      }
    }
    setMergeState(null);
  }, [mergeState, onMergeCards]);

  const handleMergeCancel = useCallback(() => {
    setMergeState(null);
  }, []);

  const handleAddCard = useCallback(
    (columnId: string) =>
      async (content: string): Promise<void> => {
        await onAddCard(columnId, content, userName);
      },
    [onAddCard, userName]
  );

  const sortedColumns = [...board.columns].sort((a, b) => a.order - b.order);
  const columnIds = sortedColumns.map((c) => c.id);
  const viewerVoteTotal = sortedColumns
    .flatMap((column) => column.cards)
    .reduce((total, card) => total + (card.viewerVoteCount ?? 0), 0);

  return (
    <>
      <DndContext
        sensors={sensors}
        collisionDetection={pointerThenCorners}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <SortableContext items={columnIds} strategy={horizontalListSortingStrategy}>
          <div
            className="flex h-full w-full items-stretch gap-5 overflow-x-auto p-6"
            data-testid="board-columns"
          >
            {sortedColumns.map((column) => (
              <ColumnComponent
                key={column.id}
                column={column}
                onAddCard={handleAddCard(column.id)}
                settings={board.settings}
                isAdmin={isAdmin}
                userId={userId}
                revealAllCards={revealAllCards}
                onEditCard={onEditCard}
                onDeleteCard={onDeleteCard}
                onVoteCard={onVoteCard}
                onRemoveCardVote={onRemoveCardVote}
                viewerVoteTotal={viewerVoteTotal}
                onEditColumn={onEditColumn ? () => onEditColumn(column) : undefined}
                onDeleteColumn={onDeleteColumn ? () => onDeleteColumn(column) : undefined}
              />
            ))}
          </div>
        </SortableContext>
        <DragOverlay>
          {activeCard && <CardOverlay card={activeCard} />}
          {activeColumn && (
            <div className="min-w-[20rem] rounded-lg border-2 border-gray-300 bg-white p-4 opacity-90 shadow-xl dark:border-gray-600 dark:bg-gray-900">
              <div className="font-semibold text-gray-900 dark:text-white">
                {activeColumn.name}
              </div>
            </div>
          )}
        </DragOverlay>
      </DndContext>

      <MergeCardModal
        isOpen={mergeState !== null}
        onClose={handleMergeCancel}
        onConfirm={handleMergeConfirm}
        sourceCard={mergeState?.sourceCard || null}
        targetCard={mergeState?.targetCard || null}
        isLoading={isMerging}
      />
    </>
  );
}
